// Globals
import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import {
  StyledLabel,
  StyledDescription,
  StyledInputWrap,
  StyledStepTitle,
  StyledSelect,
  StyledSelectWrap,
  StyledRow,
  Circle,
  StyledButton,
  StyledContinueBlock,
  StyledImportForm,
  StyledFormItem,
} from '../Styled';

// Helpers
import { Checkbox } from '@/ui';
import { IFormInstance } from '@/components';
import { useImportProductsFormRules } from '../../hooks/useImportProductsFormRules';
import { ImportProductsFieldNames } from '../../helpers/types';
import { IMPORT_INSTRUCTION_LINK } from '../../helpers/constants';

type TPreparingStepContainer = {
  onSubmit: () => void;
  categoryOptions: { value: string; label: string }[];
  partnersOptions: { value: string; label: string }[];
  onPartnerSearch: (name: string) => void;
  onCategoriesSearch: (name: string) => void;
  onGetEmptyTemplate: () => void;
  onGetPrefilledTemplate: () => void;
  formInstance: IFormInstance;
  canManage: boolean;
};

const PreparingStepContainer = ({
  onSubmit,
  onCategoriesSearch,
  partnersOptions,
  onPartnerSearch,
  onGetEmptyTemplate,
  onGetPrefilledTemplate,
  categoryOptions,
  formInstance,
  canManage,
}: TPreparingStepContainer) => {
  const { t } = useTranslation(['products', 'validations']);
  const rules = useImportProductsFormRules();

  const openInstruction = () => {
    window.open(IMPORT_INSTRUCTION_LINK, '_blank');
  };

  return (
    <>
      <StyledImportForm form={formInstance} onFinish={onSubmit}>
        <StyledStepTitle>{t('products_import_step', { step: 1 })}</StyledStepTitle>
        <StyledInputWrap>
          <StyledLabel>{t('products_import_choose_category_label')}</StyledLabel>
          <StyledSelectWrap>
            <StyledDescription>{t('products_import_choose_category_description')}</StyledDescription>
            <StyledFormItem
              name={ImportProductsFieldNames.CATEGORY_ID}
              rules={rules[ImportProductsFieldNames.CATEGORY_ID]}
            >
              <StyledSelect allowClear showSearch options={categoryOptions} onSearch={onCategoriesSearch} />
            </StyledFormItem>
          </StyledSelectWrap>
          <StyledSelectWrap>
            <StyledDescription>{t('products_import_choose_partner_description')}</StyledDescription>
            <StyledFormItem
              name={ImportProductsFieldNames.PARTNER_ID}
              rules={rules[ImportProductsFieldNames.CATEGORY_ID]}
            >
              <StyledSelect allowClear showSearch options={partnersOptions} onSearch={onPartnerSearch} />
            </StyledFormItem>
          </StyledSelectWrap>
          <StyledInputWrap>
            <StyledRow>
              <Circle>1</Circle>
              <StyledLabel>{t('products_import_instruction_1')}</StyledLabel>
            </StyledRow>
            <StyledButton onClick={onGetEmptyTemplate} disabled={!canManage}>
              {t('products_import_template_download')}
            </StyledButton>
            <StyledButton onClick={onGetPrefilledTemplate} disabled={!canManage}>
              {t('products_import_template_export')}
            </StyledButton>
          </StyledInputWrap>
          <StyledInputWrap>
            <StyledRow>
              <Circle>2</Circle>
              <StyledLabel>{t('products_import_instruction_2')}</StyledLabel>
            </StyledRow>
            <StyledButton onClick={openInstruction} disabled={!canManage}>
              {t('products_import_instruction_download')}
            </StyledButton>
          </StyledInputWrap>
          <StyledContinueBlock>
            <StyledRow>
              <StyledFormItem
                name={ImportProductsFieldNames.IS_CONFIRMED}
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('validation_message_required', { ns: 'validations' }))),
                  },
                ]}
                valuePropName="checked"
              >
                <Checkbox>{t('products_import_instruction_confirmation')}</Checkbox>
              </StyledFormItem>
            </StyledRow>
            {canManage && (
              <StyledButton type={'primary'} htmlType={'submit'}>
                {t('products_import_continue')}
              </StyledButton>
            )}
          </StyledContinueBlock>
        </StyledInputWrap>
      </StyledImportForm>
    </>
  );
};

export default PreparingStepContainer;
